import CloseIcon from '@mui/icons-material/Close';
import { Grow } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, {
  Dispatch,
  forwardRef,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DialogStyled, ToastContentBlock, TooltipStyled } from 'src/components';
import { useCurrentUser, useGetOrgUsers, useInviteUsers } from 'src/hooks';
import type { MembershipRole } from 'src/types';
import { emailExists } from 'src/utils';
import { z } from 'zod';
import { InviteesList, RoleDropdown } from '..';
// import { EmailDomainDisplay } from './EmailDomainDisplay';

type Props = {
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
};

export const InviteModal = ({ show, setShow }: Props) => {
  const { user } = useCurrentUser();
  const [emailInput, setEmailInput] = useState<string>('');
  // const [domainDisplay, setDomainDisplay] = useState<string>('');
  const [emailInputError, setEmailInputError] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const [showInputRoleDropdown, setShowInputRoleDropdown] = useState(false);
  const [roleInput, setRoleInput] = useState<MembershipRole>('member');
  const [invitees, setInvitees] = useState<
    { email: string; role: MembershipRole }[]
  >([]);

  const emailInputRef = useRef<HTMLInputElement>(null);
  const userEmailDomain = user!.email!.split('@')[1];

  const usersQuery = useGetOrgUsers();

  /**
   * Focuses the email input and places the cursor at the correct position.
   * If the input contains '@', places the cursor at the '@' character.
   * If not, places the cursor at the end of the input.
   * This ensures correct cursor behavior when clicking in and out of the input.
   */
  const focusEmailInput = useCallback(() => {
    const indexOfAt = emailInput.indexOf('@');
    emailInputRef.current?.focus();
    emailInputRef.current?.setSelectionRange(indexOfAt, indexOfAt);
  }, [emailInput]);

  useEffect(() => {
    // setDomainDisplay(`@${userEmailDomain}`);
    setEmailInput(`@${userEmailDomain}`);
  }, [userEmailDomain]);

  // Focus the input when modal opens - delayed for animation timing
  useEffect(() => {
    if (show) {
      setTimeout(() => {
        emailInputRef.current?.focus();
      }, 150);
    }
  }, [show]);

  // TODO - while isInvitingUsers is true, show a loading spinner over the list of emails to invite
  // Once it's done, show the list of failed invites and if there are no failed invites,
  // close the modal - a success toast will display in the top right of the page.
  const { mutate: inviteUsers, isPending: isInvitingUsers } = useInviteUsers({
    invitees,
    setInvitees,
    setEmailInputError,
  });

  // TODO - display cart loading icon over invitees list while the invites are being sent
  // TODO - if complete success, close modal and display a success toast
  const handleInviteUsers = () => {
    inviteUsers(invitees);
  };

  // update the role input and close the role dropdown
  const handleSelectInputRole = (role: MembershipRole) => {
    setRoleInput(role);
    setShowInputRoleDropdown(false);
    focusEmailInput();
  };

  const handleAddInvitee = () => {
    const email = emailInput.trim();
    const emailDomain = email.split('@')[1];
    const role = roleInput;

    if (!email) {
      setEmailInputError({
        title: 'Email Required',
        message: 'Please provide an email and press enter',
      });
      return;
    }

    if (!emailDomain || emailDomain !== userEmailDomain) {
      setEmailInputError({
        title: 'Invalid Email Domain',
        message: `You can only invite users from your organization's domain: ${userEmailDomain}`,
      });
      return;
    }

    if (!z.string().email().safeParse(email).success) {
      setEmailInputError({
        title: 'Invalid Email',
        message: 'Please provide a valid email and press enter',
      });
      return;
    }

    if (
      emailExists(
        usersQuery.data?.pages.flatMap((page) => page.users) || [],
        invitees,
        email,
      )
    ) {
      setEmailInputError({
        title: 'User Exists',
        message:
          'This user is already part of your organization or in the list of invites to send.',
      });
      return;
    }

    setEmailInputError(null);
    setInvitees([...invitees, { email, role }]);
    setEmailInput(`@${userEmailDomain}`);
    // setEmailInputDisplay('');
    setRoleInput('member');
    focusEmailInput();
    setTimeout(() => {
      emailInputRef.current?.setSelectionRange(0, 0);
    }, 0);
  };

  const handleUpdateInviteeRole = (email: string, role: MembershipRole) => {
    setInvitees(
      invitees.map((invitee) =>
        invitee.email === email ? { ...invitee, role } : invitee,
      ),
    );
  };

  const handleRemoveInvitee = (email: string) => {
    setInvitees(invitees.filter((invitee) => invitee.email !== email));
  };

  const handleCancel = () => {
    setShow(false);

    // wait for animation to complete before resetting the state
    setTimeout(() => {
      setEmailInput(`@${userEmailDomain}`);
      // setEmailInputDisplay('');
      setEmailInputError(null);
      setShowInputRoleDropdown(false);
      setRoleInput('member');
      setInvitees([]);
    }, 150);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text/plain');
    const domain = pastedText.split('@')[1];

    if (domain !== userEmailDomain) {
      setEmailInput(`@${userEmailDomain}`);
      setEmailInputError({
        title: 'Invalid Email Domain',
        message: `You can only invite users from your organization's domain: ${userEmailDomain}`,
      });
    } else {
      setEmailInput(pastedText);
      // setEmailInputDisplay(name);
    }
  };

  // useEffect(() => {
  //   const inputIncludesAt = emailInput.includes('@');
  //   const [name, domain] = emailInput.split('@');

  //   if (inputIncludesAt) {
  //     setDomainValue(`@${domain}`);
  //   } else {
  //     setDomainValue('');
  //   }

  //   setEmailInputDisplay(name);
  // }, [emailInput, domainValue]);

  return (
    <DialogStyled
      open={show}
      onClose={() => setShow(false)}
      TransitionComponent={Transition}
    >
      <div
        onClick={() => setShow(false)}
        className='absolute right-3 top-2.5 cursor-pointer fill-darkSteel hover:fill-gray-400'
      >
        <CloseIcon
          className='transition-all'
          style={{ fontSize: '24px', fill: 'inherit' }}
        />
      </div>
      <div className='flex w-full flex-col items-center justify-center gap-4 p-4 sm:py-6 md:gap-6 md:px-6 md:py-8'>
        <h4 className='heading-300 text-primary-dark'>Add Users</h4>
        <div className='flex w-full flex-col items-center justify-center gap-2'>
          <div className='flex w-full flex-col items-center justify-center gap-2 md:gap-4'>
            <TooltipStyled
              title='Press enter to add new user to list'
              placement='bottom'
            >
              <input
                ref={emailInputRef}
                type='text' // must be text to allow cursor positioning
                onFocus={() => focusEmailInput()} // move the cursor to the front of the input value when focused
                placeholder='Enter Email'
                className='relative h-10 w-full rounded border-none bg-offWhite py-2 pl-3 pr-12 font-secondary font-semibold leading-[24px] text-primary-dark outline-none transition-all selection:bg-primary selection:text-cloudGray placeholder:font-normal placeholder:text-[#636E7A] focus:ring-2 focus:ring-primary group-hover:bg-gray-300 group-hover:focus:bg-cloudGray xs:h-12'
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleAddInvitee();
                  }
                }}
                onPaste={handlePaste}
                // onChange={(e) => {
                //   const value = e.target.value;

                //   // backspace was pressed and we have part of the email domain in the email, otherwise below will handle it
                //   if (value.length < emailInput.length) {
                //     if (emailInput.includes('@')) {
                //       const endOfEmailInput = emailInput.at(-1);
                //       setEmailInput(value);
                //       setDomainDisplay(`${endOfEmailInput}${domainDisplay}`);
                //       return;
                //     } else {
                //       setEmailInput(value);
                //       setDomainDisplay(`@${userEmailDomain}`);
                //     }
                //   }

                //   if (value.includes('@')) {
                //     if (value.at(-1) === domainDisplay.at(0)) {
                //       setDomainDisplay(domainDisplay.slice(1));
                //       setEmailInput(value);
                //     } else {
                //       setEmailInput(value.slice(0, -1));
                //     }
                //   } else {
                //     setEmailInput(value);
                //   }
                // }}
              />
            </TooltipStyled>
            <div className='absolute right-8 flex flex-row flex-nowrap items-center justify-end gap-6'>
              {/* <div className='flex flex-row flex-nowrap items-center justify-end gap-0'>
                <EmailDomainDisplay
                  domainDisplay={`@${userEmailDomain}`}
                  setDomainDisplay={setDomainDisplay}
                  domainInput={
                    !emailInput.includes('@')
                      ? ''
                      : `@${emailInput.split('@')[1]}`
                  }
                />
              </div> */}
              <RoleDropdown
                showDropdown={showInputRoleDropdown}
                handleShowDropdown={() =>
                  setShowInputRoleDropdown(!showInputRoleDropdown)
                }
                isPending={false}
                displayPending={false}
                role={roleInput}
                handleSelectRole={handleSelectInputRole}
                dropdownId='input-role-dropdown'
                userId={emailInput}
              />
            </div>
          </div>

          {emailInputError && (
            <ToastContentBlock
              toastTitle={emailInputError.title}
              variant='error'
            >
              {emailInputError.message}
            </ToastContentBlock>
          )}
        </div>

        <InviteesList
          invitees={invitees}
          usersArePending={isInvitingUsers}
          handleUpdateRole={handleUpdateInviteeRole}
          handleRemoveInvitee={handleRemoveInvitee}
        />

        <div className='flex w-full flex-row items-center justify-between gap-2'>
          <button
            className='button-secondary h-12'
            onClick={handleCancel}
            disabled={isInvitingUsers}
          >
            {invitees.length > 0 ||
            isInvitingUsers ||
            emailInput !== `@${userEmailDomain}`
              ? 'Cancel'
              : 'Close'}
          </button>
          <button
            className='button-primary h-12'
            onClick={handleInviteUsers}
            disabled={isInvitingUsers || invitees.length === 0}
          >
            Invite {invitees.length} Users
          </button>
        </div>
      </div>
    </DialogStyled>
  );
};

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} />;
});
